/**
 * Settings Tabs Component
 * 
 * Consolidates all settings and configuration functionality into a single tabbed interface:
 * - Profile: User profile and preferences
 * - Team: Team management and analytics
 * - Chart of Accounts: Account management
 * - File Monitor: File monitoring and quality oversight
 * 
 * This replaces the need for separate TeamManagement, ChartOfAccounts, and FileMonitor pages.
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AvatarUpload } from "@/components/ui/avatar-upload";
import { User, Bell, Shield, Palette, Database as DatabaseIcon, Key, Save, Building, Mail, Calendar, Loader2, Users, FileText, BarChart3, Plug } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { getRoleDisplayName } from "@/lib/profile";
import { formatSwissCurrency } from "@/integrations/supabase/client";
import type { Database } from '@/types/database';
import PayPalIntegrationSection from './PayPalIntegrationSection';

type ProfileRole = Database['public']['Tables']['users']['Row']['role'];
type ProfilePreferences = Database['public']['Tables']['users']['Row']['preferences'];

interface SettingsTabsProps {
  className?: string;
}

export const SettingsTabs: React.FC<SettingsTabsProps> = ({ className }) => {
  const { user, profile, organization, updateProfile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
  });

  // Update form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
      });
    }
  }, [profile]);

  const [preferences, setPreferences] = useState<any>(
    (profile?.preferences as any) || {
      theme: 'light',
      language: 'de',
      notifications: {
        email: true,
        push: false,
        sms: false,
      },
      privacy: {
        profile_visibility: 'team',
        data_sharing: false,
      },
    }
  );

  const handleSaveProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      await updateProfile({
        first_name: formData.first_name,
        last_name: formData.last_name,
        preferences: preferences,
      });
      
      toast({
        title: "Profil aktualisiert",
        description: "Ihre Profildaten wurden erfolgreich gespeichert.",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Profil konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = (key: string, value: any) => {
    setPreferences((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleNestedPreferenceChange = (parent: string, key: string, value: any) => {
    setPreferences((prev: any) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: value,
      },
    }));
  };

  // Sync selected tab with URL (?tab=...)
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get('tab') || 'profile';

  const handleTabChange = (value: string) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', value);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  return (
    <div className={className}>
      <Tabs value={urlTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <i className="fa-solid fa-user"></i>
            Profil
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-2">
            <i className="fa-solid fa-users"></i>
            Team
          </TabsTrigger>
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <i className="fa-solid fa-list"></i>
            Kontenplan
          </TabsTrigger>
          <TabsTrigger value="monitor" className="flex items-center gap-2">
            <i className="fa-solid fa-monitor"></i>
            Datei-Monitor
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            <Plug className="h-4 w-4" />
            Integrationen
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Information */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-brand-primary" />
                    Profilinformationen
                  </CardTitle>
                  <CardDescription>
                    Verwalten Sie Ihre persönlichen Daten und Einstellungen.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <AvatarUpload
                      onUploadComplete={(url) => {
                        // Handle avatar change
                      }}
                    />
                    <div>
                      <h3 className="text-lg font-medium">{profile?.first_name} {profile?.last_name}</h3>
                      <p className="text-sm text-brand-text-secondary">{profile?.email}</p>
                      <Badge variant="outline" className="mt-1">
                        {getRoleDisplayName(profile?.role as ProfileRole)}
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Vorname</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        placeholder="Ihr Vorname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nachname</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        placeholder="Ihr Nachname"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      value={profile?.email || ''}
                      disabled
                      className="bg-brand-secondary"
                    />
                    <p className="text-xs text-brand-text-secondary">
                      E-Mail-Adresse kann nicht geändert werden
                    </p>
                  </div>

                  <Button onClick={handleSaveProfile} disabled={loading} className="w-full">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Speichern...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Profil speichern
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Preferences */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5 text-brand-primary" />
                    Einstellungen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Design</Label>
                      <Select
                        value={preferences.theme}
                        onValueChange={(value) => handlePreferenceChange('theme', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="light">Hell</SelectItem>
                          <SelectItem value="dark">Dunkel</SelectItem>
                          <SelectItem value="system">System</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Sprache</Label>
                      <Select
                        value={preferences.language}
                        onValueChange={(value) => handlePreferenceChange('language', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="de">Deutsch</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Benachrichtigungen
                    </h4>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="email-notifications">E-Mail</Label>
                        <Switch
                          id="email-notifications"
                          checked={preferences.notifications?.email}
                          onCheckedChange={(checked) => 
                            handleNestedPreferenceChange('notifications', 'email', checked)
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="push-notifications">Push</Label>
                        <Switch
                          id="push-notifications"
                          checked={preferences.notifications?.push}
                          onCheckedChange={(checked) => 
                            handleNestedPreferenceChange('notifications', 'push', checked)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-brand-primary" />
                Team Management
              </CardTitle>
              <CardDescription>
                Verwalten Sie Ihr Team und überwachen Sie die Leistung.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-brand-text-secondary mx-auto mb-4" />
                <h3 className="text-lg font-medium text-brand-text-primary mb-2">Team Management</h3>
                <p className="text-brand-text-secondary mb-4">
                  Team-Analysen und Management-Funktionen werden hier verfügbar sein.
                </p>
                <Button disabled className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Bald verfügbar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chart of Accounts Tab */}
        <TabsContent value="accounts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DatabaseIcon className="h-5 w-5 text-brand-primary" />
                Kontenplan
              </CardTitle>
              <CardDescription>
                Verwalten Sie Ihren Kontenplan und Buchungskonten.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <DatabaseIcon className="h-12 w-12 text-brand-text-secondary mx-auto mb-4" />
                <h3 className="text-lg font-medium text-brand-text-primary mb-2">Kontenplan</h3>
                <p className="text-brand-text-secondary mb-4">
                  Kontenplan-Management wird hier verfügbar sein.
                </p>
                <Button disabled className="flex items-center gap-2">
                  <DatabaseIcon className="h-4 w-4" />
                  Bald verfügbar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* File Monitor Tab */}
        <TabsContent value="monitor" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-brand-primary" />
                Datei-Monitor
              </CardTitle>
              <CardDescription>
                Überwachen Sie Datei-Uploads und Qualität.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <BarChart3 className="h-12 w-12 text-brand-text-secondary mx-auto mb-4" />
                <h3 className="text-lg font-medium text-brand-text-primary mb-2">Datei-Monitor</h3>
                <p className="text-brand-text-secondary mb-4">
                  Datei-Monitoring und Qualitätsüberwachung werden hier verfügbar sein.
                </p>
                <Button disabled className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Bald verfügbar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* PayPal Integration */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plug className="h-5 w-5 text-brand-primary" />
                    PayPal Integration
                  </CardTitle>
                  <CardDescription>
                    Verbinden Sie Ihr PayPal-Konto für automatische Transaktionsverarbeitung und Klassifizierung.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PayPalIntegrationSection />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
